import { apiRequest } from "src/services/apiClient";
import type { ParsedGdtPatient } from "src/services/gdtParser";

/** Staff's explicit billing-recipient decision from the review popup — mirrors
 *  backend/src/services/gdtSync/upsertGdtPatient.ts's GdtRecipientResolution. Omitted
 *  entirely when the patient already has a known recipient (nothing to decide). */
export type GdtRecipientResolution =
    | { mode: "self" }
    | { mode: "existing"; recipientId: string }
    | {
          mode: "new";
          /** Reuse a recipient already on file for this person instead of creating one (the
           *  automatic guardian path); left out when staff explicitly choose "create new". */
          matchExisting?: boolean;
          newRecipient: {
              salutation?: string;
              title?: string;
              firstName?: string;
              lastName: string;
              email?: string;
              street?: string;
              plz?: string;
              city?: string;
              tel?: string[];
              birthDate?: string; // ISO yyyy-mm-dd
              gender?: string; // M / F / D
          };
      };

/** Mirrors backend/src/services/gdtSync/upsertGdtPatient.ts's GdtRevertInfo — everything
 *  needed to undo exactly one sync write via cancelGdtSync below. */
export interface GdtRevertInfo {
    patient: { wasCreated: boolean; priorFields?: Record<string, any> };
    recipient: { id: string; wasCreated: boolean; priorFields?: Record<string, any> } | null;
}

export interface GdtSyncResult {
    patientId: string;
    recipientId: string;
    created: boolean;
    // A minor synced with no recipient data in the GDT record — left without a
    // billing recipient rather than guessed, staff must assign one manually.
    needsRecipient: boolean;
    // Who recipientId actually belongs to — lets the review popup show "QR/consent goes to
    // X" without a second round trip. Undefined when recipientId is empty.
    recipientFirstName?: string;
    recipientLastName?: string;
    recipientEmail?: string;
    revertInfo: GdtRevertInfo;
}

/** Sends a (staff-reviewed, possibly edited) .gdt patient record to the backend upsert
 *  endpoint (POST /api/gdt-sync/patient — see backend/src/controllers/gdtSyncController.ts).
 *  Nothing is written server-side until this is called — parsing alone never commits. */
export function syncGdtPatient(
    patient: ParsedGdtPatient,
    recipientResolution?: GdtRecipientResolution,
): Promise<GdtSyncResult> {
    return apiRequest<GdtSyncResult>(
        "/gdt-sync/patient",
        { method: "POST", body: JSON.stringify({ ...patient, recipientResolution }) },
        { "Content-Type": "application/json" },
    );
}

/** Undoes exactly one syncGdtPatient write (POST /api/gdt-sync/patient/cancel — see
 *  cancelGdtSync in backend/src/controllers/gdtSyncController.ts). The review popup may have
 *  called syncGdtPatient more than once for the same file (the initial commit, then again
 *  if staff resolved a fallback recipient) — callers revert each recorded revertInfo in
 *  reverse order so a mid-review recipient change doesn't leave the original create behind. */
export function cancelGdtSync(patientId: string, revertInfo: GdtRevertInfo): Promise<{ ok: boolean }> {
    return apiRequest<{ ok: boolean }>(
        "/gdt-sync/patient/cancel",
        { method: "POST", body: JSON.stringify({ patientId, revertInfo }) },
        { "Content-Type": "application/json" },
    );
}

/** Set when the desktop agent already committed this record (info/Sync/praxis-software-sync-exe-plan.md
 *  §5b) — the browser review popup then says so instead of looking like the first time this patient
 *  was processed. */
export interface AgentCommitInfo {
    committedAt: string;
}

/** Asks the backend whether the desktop agent recently auto-committed this externalId. Fails open —
 *  a lookup error must never block the staff review, so any failure just means "no note". A commit
 *  staff already undid is not reported either, since "processed by the agent" would be untrue. */
export async function fetchRecentAgentCommit(externalId: string): Promise<AgentCommitInfo | null> {
    try {
        const res = await apiRequest<{ committed: boolean; committedAt?: string; undoneAt?: string | null }>(
            `/gdt-agent-sync/recent-commit?externalId=${encodeURIComponent(externalId)}`,
        );
        return res.committed && !res.undoneAt && res.committedAt ? { committedAt: res.committedAt } : null;
    } catch {
        return null;
    }
}
